<template>
  <div class="eligibility-uploader">
    <div class="import-card">
      <div class="card-header">
        <h2>Upload Tool</h2>
        <md-button @click="handleTemplateDownload">
          <i>Download Template File</i>
        </md-button>
      </div>
      <div class="card-body">
        <div class="instructions">
          <h5>External Donations</h5>
        </div>
        <div class="selections-container">
          <div v-if="externalSelectedFile" class="current-file">
            <span class="label">Selected File:</span>
            <span class="file-name">{{ externalSelectedFile.name }}</span>
          </div>
          <select
            v-if="user.isStaff && !organizations.refreshing"
            class="organizations"
            @change="onExternalDonationOrganizationSelection"
          >
            <option value="" disabled :selected="externalDonationSelectedOrganization.id === null"
              >Select Organization</option
            >
            <option
              v-for="org in organizations.list"
              :key="org.id"
              :value="org.id"
              :selected="externalDonationSelectedOrganization.id === org.id"
              >{{ org.name }}</option
            >
          </select>
          <div
            v-else-if="user.isStaff && organizations.refreshing"
            class="organizations placeholder"
          >
            <LoadSpinner />
          </div>
        </div>
        <div v-if="externalErrors" class="errors-container">
          <p v-for="error in externalErrors" :key="error" class="error">{{ error }}</p>
        </div>
        <div class="buttons-container">
          <md-button class="choose-file-button" @click="handleExternalDonationUploadClick">
            <input
              type="file"
              accept=".csv"
              hidden
              ref="externalfile"
              @change="handleExternalUpload"
            />
            <i>Choose File</i>
          </md-button>
          <md-button
            v-if="isExternalDonationProceedRendered"
            class="proceed-button"
            @click="onExternalDonation"
            :disabled="isExternalDonationProceedDisabled"
          >
            <i v-if="!externalDonation.loading">Proceed</i>
            <span v-else>
              <LoadSpinner :color="'white'" />
            </span>
          </md-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { LoadSpinner } from '@/components'
import ExternalDonations from '@/services/externalDonations'
import CollectionManager from '@/services/collectionManager'
import OrganizationModel from '@/services/organizations'
import Pagination from '@/services/pagination'

// this is used to employ resetViewState()
function getDefaultData(nonResettingState = {}) {
  return {
    ...nonResettingState,
    externalSelectedFile: null,
    externalErrors: null,
    externalDonationSelectedOrganization: {
      name: null,
      id: null,
    },
    externalDonation: {
      loading: false,
    },
  }
}

const nonResettingState = {
  organizations: CollectionManager.create({
    ModelClass: OrganizationModel,
    pagination: Pagination.create({ pageSize: 40 }),
    refreshing: true,
    // filters: { ordering: '-datetime_created' }, TODO(Bruno 4-3-2020): filter alphabetically
  }),
}

export default {
  name: 'Uploader',
  components: { LoadSpinner },
  data() {
    return getDefaultData(nonResettingState)
  },
  created() {
    this.fetchOrganizations()
  },
  methods: {
    fetchOrganizations() {
      if (this.user.isStaff) {
        this.organizations.refresh()
      }
    },
    handleTemplateDownload() {
      window.open('/static/employee-codes-template.csv')
    },
    handleExternalDonationUploadClick() {
      this.$refs.externalfile.click()
    },
    handleExternalUpload(e) {
      this.externalSelectedFile = e.target.files[0]
      this.externalErrors = null
    },
    onExternalDonation() {
      this.externalDonation.loading = true
      let options = {
        isStaff: this.user.isStaff,
        organization: this.externalDonationSelectedOrganization.id,
      }
      ExternalDonations.api
        .uploadCSV(this.externalSelectedFile, options)
        .then(() => {
          this.success = true
          this.$Alert.alert({
            type: 'success',
            message: `<h4>Import Successful!</h4>`,
            timeout: 6000,
          })
          this.resetViewState()
        })
        .catch(this.uploadExternalErrorHandler)
        .finally(() => {
          this.externalDonation.loading = false
        })
    },
    onExternalDonationOrganizationSelection({ target: { value } }) {
      let targetOrg = this.organizations.list.find(org => org.id === value)
      this.externalDonationSelectedOrganization = {
        name: targetOrg.name,
        id: value,
      }
    },
    resetViewState() {
      this.$refs.externalfile.value = null
      Object.assign(this.$data, getDefaultData())
    },
    uploadExternalErrorHandler(error) {
      if (error.response.status >= 500) {
        this.externalErrors = error.response.data.split('\n').slice(0, 2)
        return
      }
      if (Array.isArray(error.response.data)) {
        this.externalErrors = error.response.data
      } else {
        this.externalErrors = [error.response.data]
      }
    },
  },
  computed: {
    ...mapState(['user']),
    isExternalDonationProceedRendered() {
      if (this.user.isAdmin) {
        return !!this.externalSelectedFile
      }

      return this.externalSelectedFile && !!this.externalDonationSelectedOrganization.id
    },
    isExternalDonationProceedDisabled() {
      if (!this.externalSelectedFile) {
        return true
      }

      if (this.user.isStaff) {
        return !this.externalDonationSelectedOrganization.id
      } else {
        return !this.user.adminPermissions.canEditEmployeeCodes
      }
    },
    isConfirmDisabled() {
      return this.externalDonation.loading
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../styles/variables';
@import '../../styles/mixins/containers';

.eligibility-uploader {
  @include screen-container($mobile-padding: 1rem 0.5rem, $desktop-max-width: 112rem);
  display: flex;
  flex-flow: row;
  align-items: center;
  justify-content: center;
}

.import-card {
  width: 80%;
  margin-left: auto;
  margin-right: auto;
  border: 1px solid #e4eeee;
  border-radius: 5px;
}

.card-header {
  display: flex;
  flex-flow: row;
  align-items: center;
  padding: 0 30px;
  border-bottom: 1px solid #e4eeee;

  h2 {
    margin: 30px auto 30px 0;
    font-weight: 800;
  }

  .md-button {
    background-color: #fff;
    color: #a1a1a6;
    border: solid 1px #a1a1a6;
    border-radius: 5px;
  }
}

.card-body {
  padding: 30px 30px;

  p {
    margin-bottom: 30px;
  }
}

.copy-list {
  margin-bottom: 2rem;
}

.emphasis {
  display: inline;
  text-decoration: underline;
  font-weight: bold;
}

.top-margin {
  margin-top: 1.5rem;
}

.selections-container {
  margin-bottom: 30px;
  display: flex;
  flex-flow: row;
  align-items: center;

  .organizations {
    margin-left: auto;
    width: 300px;
    height: 36px;
    font-family: $base-font-family;
    font-size: 1rem;
  }

  .placeholder {
    margin-left: auto;
    width: 300px;
    height: 36px;
    font-family: $base-font-family;
    font-size: 1rem;
    border-radius: 5px;
    border: 1px solid #a6a6a6;
    background-color: #f8f8f8;
    display: flex;
    flex-flow: row;
    align-items: center;
    justify-content: center;
  }
}

.current-file {
  .label {
    margin-right: 25px;
    font-size: 1rem;
  }

  .file-name {
    font-size: 1rem;
  }
}

.errors-container {
  .error {
    color: $ruby;
  }
}

.buttons-container {
  display: flex;
  flex-flow: row;
  align-items: center;

  .choose-file-button {
    margin-right: auto;
    background-color: #000;
    color: #fff;
    border-radius: 5px;

    &:disabled {
      background-color: $mid-gray;
    }
  }

  .confirm-upload {
    margin-left: auto;
    margin-right: 2rem;
  }

  .proceed-button {
    background-color: $blue;
    color: #fff;
    border-radius: 5px;

    &:disabled {
      background-color: $mid-gray;
    }
  }
}

.success-header {
  text-align: center;
  font-weight: 800;
}

.list-container {
  margin-bottom: 30px;

  ul {
    margin-left: 50px;

    li {
      font-family: $base-font-family;
      margin: 5px 0;
    }
  }

  p {
    font-family: $base-font-family;
    margin-left: 50px;
  }
}

.button-container {
  display: flex;
  flex-flow: row;

  .md-button {
    margin-left: auto;
    background-color: #000;
    color: #fff;
    border-radius: 5px;
  }
}

.code-list {
  background-color: $soft-gray;
  border: 1px solid $gray;
  border-radius: 3px;
  margin-top: 0.5rem;
  padding-bottom: 0.5rem;
  padding-left: 1rem;
  max-height: 10rem;
  overflow-y: auto;
}
</style>
